import { isSettingsDeferredOwnerPath } from '@/lib/settings/settings-deferred-owner';
import { resolveSettingsAccess } from '@/lib/settings/settings-resolver';
import type { SettingsDelegationPreference } from '@/lib/settings/settings-delegation';
/** Parent-route redirects SSOT: ./parent-route-redirects.json */
/**
 * Navigation v2 — Information Architecture (single source of truth).
 *
 * Port of `nav-redesign/data.jsx` `NAV` + `FOOTER_NAV` with two overlays:
 *
 *   1. Href rewrites (Phase 0 route-audit §A + Q7 resolution): 13 hrefs in
 *      the prototype don't map 1:1 to a live route. See REWRITTEN_HREFS
 *      below for the mapping.
 *   2. The prototype's 7-alias role vocabulary (COORDINATOR / DON / QUALITY /
 *      FACILITY / FINANCE / EXEC / CARE_WORKER) is a coarse first pass.
 *      Authoritative filtering uses `isRoleAllowedForPath` and
 *      `isModuleNavigationVisible` from the live RBAC + entitlement layer
 *      (resolved in the rendering layer, not here).
 *
 * Per spec §0 hard constraints:
 *   - Routes do not move. Every href below resolves to an existing live page.
 *   - RBAC is read from the server. This file declares *intent*; the live
 *     RBAC layer is the gate.
 *   - Module entitlements are authoritative.
 *   - No new tables.
 */

import type { LucideIcon } from 'lucide-react';
import {
  COMPLIANCE_IA_GROUPS,
  COMPLIANCE_IA_ROUTES,
  type ComplianceIaGroupId,
} from '@statura/utils/compliance-ia';
import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  Award,
  Banknote,
  Bandage,
  BarChart3,
  Bell,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarRange,
  CheckSquare,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FileBarChart,
  FileCheck,
  FileStack,
  FileText,
  HeartPulse,
  Home,
  Landmark,
  Layers,
  ListChecks,
  ListTree,
  Lock,
  Map,
  MessageSquareWarning,
  OctagonAlert,
  PiggyBank,
  PieChart,
  Scroll,
  Settings,
  Siren,
  ShieldAlert,
  ShieldCheck,
  Star,
  Stethoscope,
  TrendingUp,
  UserCheck,
  Users,
  UsersRound,
  Wallet,
  Workflow,
} from 'lucide-react';

import { isRoleAllowedForPath } from '@/lib/route-access';
import { isModuleNavigationVisible } from '@/lib/module-access';
import { isDashboardProductionSurfaceVisibleForTenant } from '@/lib/production-surface-access';
import { isTenantNavigationPathVisible } from '@/lib/navigation/tenant-navigation';
import { isLaunchPrimaryNavigationVisible } from '@/lib/launch-navigation';
import {
  isGovernmentIntegrationPathVisible,
  type GovernmentIntegrationEnablement,
} from '@/lib/integrations/government/enablement-types';
import type { ProviderProgramContext } from './provider-type';

// ─── Role + program vocabulary ─────────────────────────────────────────────

/**
 * Prototype role aliases (data.jsx). These are *intent* signals — the
 * authoritative filter is the live `isRoleAllowedForPath` table in
 * `apps/web/src/lib/route-access.ts`. An item that passes alias filtering
 * but is denied by `isRoleAllowedForPath` MUST NOT appear in the sidebar.
 *
 * Alias → live-role mapping recorded in `docs/nav-v2/decisions.md` §Q6.
 */
export type RoleAlias =
  | 'COORDINATOR'
  | 'DON'
  | 'QUALITY'
  | 'FACILITY'
  | 'FINANCE'
  | 'EXEC'
  | 'CARE_WORKER';

export const ALL_ROLE_ALIASES: readonly RoleAlias[] = [
  'COORDINATOR',
  'DON',
  'QUALITY',
  'FACILITY',
  'FINANCE',
  'EXEC',
  'CARE_WORKER',
] as const;

/** Australian aged-care provider programs. */
export type ProgramTag = 'RAC' | 'SAH';

/**
 * Provider presentation scope. RAC/SAH are selected current lenses. MIXED
 * means multiple exact current families exist but no validated lens is active,
 * so it deliberately carries no program tags. UNKNOWN is also fail-closed.
 */
export type ProviderType = 'RAC' | 'SAH' | 'MIXED' | 'OTHER' | 'UNKNOWN';

export const PROVIDER_TAGS: Record<ProviderType, readonly ProgramTag[]> = {
  RAC: ['RAC'],
  SAH: ['SAH'],
  // A raw provider type cannot distinguish the legacy unresolved chooser from
  // an accepted Mixed workspace. `itemMatchesPrograms` deliberately keeps the
  // raw MIXED path closed; the context-aware path admits these only when the
  // server accepted workspace === 'MIXED'.
  MIXED: ['SAH', 'RAC'],
  OTHER: [],
  UNKNOWN: [],
};

// ─── Types ─────────────────────────────────────────────────────────────────

export type NavBadgeKey =
  | 'sirs_open_24h'
  | 'mar_unsigned_today'
  | 'clinical_alerts_active'
  | 'care_plans_due_30d'
  | 'claims_pending'
  | 'hr_documents_review_due'
  | 'people_cannot_work'
  | 'tasks_due_today';

export type NavBadgeCounts = Partial<Record<NavBadgeKey, number>>;

export interface NavV2Item {
  /** Display label. */
  label: string;
  /** Live route. MUST resolve to an existing page under `apps/web/src/app/(dashboard)/`. */
  href: string;
  /** Lucide icon component. */
  icon: LucideIcon;
  /** Prototype role aliases. Intent only — live RBAC overrides. */
  roles: readonly RoleAlias[];
  /** Narrow additive access for specific live roles outside the aliases. */
  additionalLiveRoles?: readonly string[];
  /** Read-only payroll discovery for eligible roster reviewers on new payroll. */
  payrollStatusOnly?: boolean;
  /** Program tags. Omitted = applies to every provider type. */
  programs?: readonly ProgramTag[];
  /** Long-form copy for command-palette descriptions. */
  description?: string;
  /** Optional badge key, resolved by the shell's scoped count loader. */
  badgeKey?: NavBadgeKey;
  /** Five-group compliance presentation identity within its existing L1 domain. */
  complianceGroupId?: ComplianceIaGroupId;
  /** Five-group compliance presentation label within its existing L1 domain. */
  complianceGroupTitle?: string;
}

export type NavV2GroupId =
  | 'home'
  | 'care'
  | 'ops'
  | 'workforce'
  | 'quality'
  | 'governance'
  | 'finance'
  | 'insights'
  // Grouped compliance destinations shown only on compliance-only plans.
  | 'compliance-only'
  | 'compliance-events'
  | 'compliance-quality'
  | 'compliance-governance'
  | 'compliance-people';

export interface NavV2Group {
  id: NavV2GroupId;
  title: string;
  icon: LucideIcon;
  description: string;
  items: readonly NavV2Item[];
}

// ─── Href rewrite registry ─────────────────────────────────────────────────
// Rationale recorded in docs/nav-v2/route-audit.md §A, §B and decisions.md
// Q7. Each entry below maps a prototype href to its live counterpart. Read
// alongside that doc when modifying.

export const REWRITTEN_HREFS: Readonly<Record<string, string>> = Object.freeze({
  // §A — different live path than prototype guessed
  '/compliance/audits': '/compliance/audit-program',
  '/compliance/aihw-qi': '/compliance/quality-indicators?tab=quarter',
  '/compliance/risk-register': '/compliance/governance/risk-register',
  '/compliance/skills-matrix': '/compliance/governance/skills-matrix',
  '/compliance/conflict-of-interest': '/compliance/governance/conflicts',
  '/finance/deposits': '/compliance/prudential/deposits',
  // §B (Q7) — parent-without-landing-page rewrites to canonical child
  '/hr': '/people',
  '/home-care': '/home-care/service-delivery',
  '/pricing': '/pricing/rate-cards',
  '/workflows': '/workflows/instances',
  '/finance/budgets': '/finance/budgets/carry-over',
  // Legacy SAH finance alias kept as a rewrite only. Visible navigation uses
  // the canonical /home-care money surfaces.
  '/finance/sah': '/home-care/budgets',
  // Dual-impl 4a–4c (2026-08-04): legacy dual hosts collapsed to middleware.
  // Keep rewrite entries so any leftover nav/palette/prototype href resolves
  // to the People IA host at construction time (middleware still covers bookmarks).
  '/staff': '/people/workers',
  '/staff/new': '/people/workers/new',
  '/staff/bulk-invite': '/people/workers/bulk-invite',
  '/staff/training': '/people/learning',
  '/staff/compliance': '/people/compliance',
  '/hr/training': '/people/learning',
  '/hr/training/dashboard': '/people/learning/analytics',
  '/hr/training/courses': '/people/learning/courses',
  '/hr/offboarding': '/people/offboarding',
  '/hr/casual-conversion': '/people/development/casual-conversion',
  '/compliance/screening': '/people/compliance/screening',
  '/compliance/certifications': '/people/compliance/certifications',
  '/compliance/workforce': '/people/compliance',
});

/**
 * Prototype hrefs intentionally OMITTED from primary nav. See decisions.md Q7.
 * Surfaced via Cmd-K (Phase 3 union strategy) once a static child exists.
 */
export const OMITTED_FROM_PRIMARY_NAV: ReadonlySet<string> = new Set([]);

// ─── Helpers ───────────────────────────────────────────────────────────────

/**
 * Does this item render under the current provider type? Port of
 * `itemMatchesPrograms` from nav-redesign/data.jsx (Q9 resolution: verbatim).
 *
 * - No `programs` field → renders for all providers.
 * - `programs: ['RAC']` → renders only for an active RAC lens.
 * - `programs: ['SAH']` → renders only for an active SAH lens.
 * - MIXED/UNKNOWN/OTHER → shared, untagged items only.
 */
export function itemMatchesPrograms(
  item: Pick<NavV2Item, 'programs'>,
  provider:
    | ProviderType
    | Pick<ProviderProgramContext, 'workspace' | 'admittedFirewalls' | 'providerType'>,
): boolean {
  if (!item.programs || item.programs.length === 0) return true;
  if (typeof provider !== 'string' && provider.workspace != null) {
    return item.programs.some((program) => provider.admittedFirewalls?.includes(program));
  }
  const providerType = typeof provider === 'string' ? provider : provider.providerType;
  // Callers without the trusted workspace marker cannot prove that MIXED is
  // the rollout workspace rather than the legacy unresolved chooser.
  if (providerType === 'MIXED') return false;
  const allowed = PROVIDER_TAGS[providerType];
  return item.programs.some((p) => allowed.includes(p));
}

// ─── IA — 8 domains (Workforce added 2026-08-04, D1 Option B) ─────────────
// Item-level rationale: docs/nav-v2/OPTIMAL-NAV-ARCHITECTURE-2026-08-04.md
// (Accepted) + docs/nav-v2/decisions.md D1–D14. Hrefs reflect REWRITTEN_HREFS.

function n(
  label: string,
  href: string,
  icon: LucideIcon,
  opts: Partial<
    Pick<
      NavV2Item,
      | 'roles'
      | 'additionalLiveRoles'
      | 'programs'
      | 'description'
      | 'badgeKey'
      | 'payrollStatusOnly'
    >
  > = {},
): NavV2Item {
  // Apply the live-route rewrite map once at construction time. If the href
  // is in OMITTED_FROM_PRIMARY_NAV, callers should not pass it here — but as
  // a safety net we throw so the bug surfaces in unit tests rather than as a
  // silent 404 in production.
  if (OMITTED_FROM_PRIMARY_NAV.has(href)) {
    throw new Error(
      `nav-v2: href "${href}" is in OMITTED_FROM_PRIMARY_NAV and must not be added to primary nav.`,
    );
  }
  const liveHref = REWRITTEN_HREFS[href] ?? href;
  return {
    label,
    href: liveHref,
    icon,
    roles: opts.roles ?? ALL_ROLE_ALIASES,
    additionalLiveRoles: opts.additionalLiveRoles,
    payrollStatusOnly: opts.payrollStatusOnly,
    programs: opts.programs,
    description: opts.description,
    badgeKey: opts.badgeKey,
  };
}

/**
 * Web-only display metadata for the pure compliance IA.  Role aliases retain
 * the former sidebar intent; the real route/module/manifest predicate remains
 * the authority for every generated destination.
 */
const COMPLIANCE_NAV_DISPLAY: Readonly<
  Record<
    string,
    Partial<Pick<NavV2Item, 'roles' | 'programs' | 'badgeKey' | 'description'>> & {
      icon: LucideIcon;
    }
  >
> = {
  '/compliance': { icon: ShieldCheck },
  '/compliance/work': {
    icon: ListChecks,
    description:
      'Your overdue, due-soon and unassigned compliance work across every register you can see',
  },
  '/compliance/calendar': { icon: Calendar },
  '/compliance/incidents': {
    icon: AlertTriangle,
    badgeKey: 'sirs_open_24h',
    description: 'SIRS Priority 1 (24h) / Priority 2 (30d) countdown',
  },
  '/compliance/complaints': { icon: MessageSquareWarning },
  '/compliance/restrictive-practices': {
    icon: OctagonAlert,
    roles: ['QUALITY', 'EXEC', 'DON', 'FACILITY', 'COORDINATOR'],
  },
  '/compliance/whs': { icon: ShieldAlert },
  '/compliance/ipc': { icon: ShieldCheck },
  '/compliance/data-breach': { icon: Lock },
  '/compliance/continuous-improvement': { icon: TrendingUp },
  '/compliance/governance/risk-register': { icon: ListTree, roles: ['QUALITY', 'EXEC'] },
  '/compliance/standards': { icon: Award },
  '/compliance/quality-indicators': { icon: Award },
  '/compliance/clinical-governance': { icon: Stethoscope },
  '/compliance/audit-program': {
    icon: ClipboardCheck,
    roles: ['QUALITY', 'EXEC', 'FACILITY', 'COORDINATOR'],
    programs: ['SAH', 'RAC'],
  },
  '/compliance/documents': {
    icon: FileStack,
    roles: ['QUALITY', 'EXEC', 'FACILITY', 'COORDINATOR', 'DON'],
  },
  '/compliance/reporting': { icon: FileBarChart },
  '/compliance/rn-coverage': { icon: Stethoscope, programs: ['RAC'] },
  '/compliance/sah-evidence': { icon: FileCheck, programs: ['SAH'] },
  '/compliance/star-ratings': {
    icon: Star,
    roles: ['EXEC', 'QUALITY', 'FACILITY'],
    programs: ['RAC'],
  },
  '/compliance/governance': { icon: Landmark },
  '/compliance/responsible-persons': { icon: UserCheck, roles: ['EXEC', 'QUALITY'] },
  '/compliance/registration': { icon: BookOpen, roles: ['EXEC', 'QUALITY'] },
  '/compliance/policies': { icon: Scroll },
  '/compliance/whistleblower': { icon: MessageSquareWarning },
  '/compliance/audit-trail': { icon: ClipboardCheck },
  '/compliance/prudential': { icon: Landmark, roles: ['EXEC', 'FINANCE'] },
  '/compliance/prudential/reports': {
    icon: FileBarChart,
    roles: ['EXEC', 'FINANCE'],
    programs: ['RAC'],
  },
  '/compliance/prudential/accommodation-agreements': {
    icon: FileCheck,
    roles: ['EXEC', 'FINANCE'],
    programs: ['RAC'],
  },
  '/compliance/capability-framework': { icon: UsersRound },
  '/compliance/ndis-overlay': { icon: Layers },
  '/compliance/record-recovery': { icon: FileCheck },
};

/** Icons for canonical compliance routes, including generated Cmd-K deep links. */
export const COMPLIANCE_NAV_ICON_BY_HREF: Readonly<Record<string, LucideIcon>> = Object.fromEntries(
  Object.entries(COMPLIANCE_NAV_DISPLAY).map(([href, display]) => [href, display.icon]),
);

const COMPLIANCE_GROUP_BY_HREF: Readonly<Record<string, (typeof COMPLIANCE_IA_GROUPS)[number]>> =
  Object.fromEntries(
    COMPLIANCE_IA_GROUPS.flatMap((group) =>
      group.routes.map((route) => [route.href, group] as const),
    ),
  );

/**
 * Keeps the eight established L1 domains and their existing door set while
 * deriving each compliance item's group label from the shared IA registry.
 * The label lets the sidebar organise the existing domain popovers into the
 * five provider-facing groups without promoting a folded route to L1.
 */
function complianceNavItem(
  href: string,
  opts: Partial<Pick<NavV2Item, 'roles' | 'programs' | 'description' | 'badgeKey'>> = {},
): NavV2Item {
  const route = COMPLIANCE_IA_ROUTES.find((candidate) => candidate.href === href);
  const group = COMPLIANCE_GROUP_BY_HREF[href];
  const display = COMPLIANCE_NAV_DISPLAY[href];
  if (!route || !group || !display) {
    throw new Error(`Missing compliance IA navigation metadata for ${href}`);
  }
  return {
    ...n(route.label, href, display.icon, { ...display, ...opts }),
    complianceGroupId: group.id,
    complianceGroupTitle: group.title,
  };
}

/**
 * The five-group registry is the density authority. A route becomes an L1
 * leaf only when its group explicitly marks it, within that group's ceiling.
 */
function complianceL1Items(groupIds: readonly ComplianceIaGroupId[]): readonly NavV2Item[] {
  return COMPLIANCE_IA_GROUPS.filter((group) => groupIds.includes(group.id)).flatMap((group) => {
    const l1Routes = group.routes.filter((route) => route.l1);
    if (l1Routes.length > group.maxL1Routes) {
      throw new Error(`Compliance IA group ${group.id} exceeds its L1 ceiling`);
    }
    return l1Routes.map((route) => complianceNavItem(route.href));
  });
}

export const NAV_V2: readonly NavV2Group[] = [
  {
    id: 'home',
    title: 'Home',
    icon: Home,
    description: 'Today, my work, command centre',
    items: [
      n('Home', '/overview', Home, { description: 'Your daily home' }),
      n('My tasks', '/tasks', CheckSquare, {
        badgeKey: 'tasks_due_today',
        description: 'Tasks assigned to you',
      }),
      n('My roster', '/me/roster', Calendar, {
        roles: ['CARE_WORKER', 'COORDINATOR', 'DON', 'FACILITY'],
        description: 'Your upcoming shifts',
      }),
      // people-workforce-route-convergence.test.ts pins exactly ONE
      // consolidated worker-readiness nav entry — do not add a separate "My
      // Compliance" item. Wave 5 instead widens the /me/work-readiness
      // module-access.ts alias so this same entry is reachable for an
      // Essentials tenant that has /me/compliance but not /me/training.
      n('My Work Readiness', '/me/work-readiness', ClipboardCheck, {
        roles: ['CARE_WORKER', 'COORDINATOR', 'DON', 'FACILITY'],
        description:
          'Onboarding, screening, credentials, learning, policies and development actions',
      }),
      n('My Caseload', '/dashboards/caseload', Users, {
        roles: ['COORDINATOR', 'DON'],
        description: 'Clients you coordinate',
      }),
      n('Clinical Dashboard (DON)', '/dashboards/don-clinical', Stethoscope, {
        roles: ['DON'],
        programs: ['RAC'],
      }),
      n('Executive View', '/dashboards/executive', BarChart3, {
        roles: ['EXEC', 'FACILITY'],
        description: 'Multi-facility KPIs',
      }),
      n('Deadlines Heatmap', '/compliance/calendar?view=agenda', Clock, {
        roles: ['COORDINATOR', 'QUALITY', 'EXEC', 'FACILITY'],
      }),
      // Density: huddle, shift offers/swaps, timesheets, messages, pay docs, leave,
      // casual conversion, hours, preferences, contractor portal, workforce analytics,
      // region roll-up → Cmd-K / me menu.
    ],
  },
  {
    id: 'care',
    title: 'Care',
    icon: HeartPulse,
    description: 'Clients, plans, clinical, monitoring',
    items: [
      // ── 1. Client foundation ─────────────────────────────────────────
      n('Clients', '/clients', Users, { description: 'Client register' }),
      n('All care plans', '/care-plans', ClipboardList, {
        badgeKey: 'care_plans_due_30d',
        description: 'Every client care plan — records and the plan editor',
      }),
      n('Assessments', '/assessments', FileCheck, {
        roles: ['COORDINATOR', 'FACILITY', 'DON', 'EXEC'],
      }),
      // ── Clinical workflow (density: secondary clinical tools → Cmd-K) ─
      n('Observations', '/observations', Activity),
      n('Clinical alerts', '/clinical/alerts', Bell, {
        badgeKey: 'clinical_alerts_active',
        roles: ['DON', 'COORDINATOR', 'FACILITY', 'CARE_WORKER'],
        description: 'Automatic alerts when an observation breaches client thresholds',
      }),
      n('Escalations', '/clinical/escalations', Siren, {
        roles: ['DON', 'COORDINATOR', 'FACILITY'],
        description: 'Frontline escalation board',
      }),
      n('Handovers', '/clinical/handovers', ArrowLeftRight, {
        roles: ['DON', 'COORDINATOR', 'FACILITY'],
        description: 'ISBAR handover ledger',
      }),
      n('Wounds', '/wounds', Bandage, { roles: ['DON', 'CARE_WORKER'] }),
      // Supervision → Cmd-K / People development tabs (density DON/RAC care ≤10).
      // ── Medications ──────────────────────────────────────────────────
      n('MAR', '/medications/mar', ClipboardCheck, {
        roles: ['DON', 'CARE_WORKER'],
        badgeKey: 'mar_unsigned_today',
        description: 'Medication administration record',
      }),
      n('S8 Register', '/medications/s8-register', Lock, {
        roles: ['DON'],
        programs: ['RAC'],
        description: 'Schedule 8 controlled drugs',
      }),
      // ── Intake / program ─────────────────────────────────────────────
      n('CM time', '/cm-tasks', Clock, {
        roles: ['COORDINATOR', 'FACILITY'],
        programs: ['SAH'],
        description: 'Log coordination time (not visits)',
      }),
      // Waitlist → Cmd-K (live-RBAC density: COORDINATOR multi-hat + CARE_WORKER
      // clinical leaves already fill Care to the ≤10 ceiling).
      // Demoted from L1 (still Cmd-K): waitlist, bulk re-plan, assessment library,
      // palliative pathways, med incidents, RN caseload, delegations, pathway
      // enrolments, restorative, overdue assessments, med discrepancies,
      // monitoring, telehealth, programs catalogue.
    ],
  },
  {
    id: 'ops',
    title: 'Operations',
    icon: Briefcase,
    // D1 Option B (2026-08-04): workforce leaves moved to dedicated Workforce domain.
    description: 'Rostering, facility delivery, regions, workflows',
    items: [
      // ONE DOOR (2026-08-18): /roster 307s to the dispatch board; the nav
      // home entry names the live page directly.
      n('Roster', '/roster/dispatch', CalendarRange, { description: 'Schedule shifts' }),
      // Two surfaces, named for WHO THEY SERVE (2026-09-09). They used to be
      // 'Worker availability' and 'My availability', which differ by one word,
      // sit adjacent, and both read as "a worker's availability" — two people
      // emailed this month unable to find availability at all.
      n('Team availability', '/workforce/availability', CalendarCheck, {
        // Empty alias set defers visibility entirely to live RBAC
        // (route-access.ts → ROSTER_MANAGE_ALLOWED_ROLES).
        roles: [],
        description: 'Unavailability for anyone you roster',
      }),
      n('My availability', '/me/availability', CalendarClock, {
        description: 'When you are unavailable, and what your coordinator recorded',
      }),
      // 2026-09-23: finance officers are route-allowed on timesheets
      // (workforce.payroll.review) but had no menu entry, so Operations showed
      // them almost nothing. Admins holding the FINANCE alias see it too;
      // coordinators reach timesheets from the roster tabs.
      n('Timesheets', '/roster/timesheets', Clock, {
        roles: ['FINANCE'],
        description: 'Review, approve and recompute worked time before pay runs',
      }),
      n('Payroll status', '/payroll', Banknote, {
        roles: ['COORDINATOR'],
        payrollStatusOnly: true,
        description: 'Check payroll status and fix held timesheets',
      }),
      n('Residential', '/residential', Building2, {
        roles: ['EXEC', 'FACILITY', 'DON', 'COORDINATOR'],
        programs: ['RAC'],
        description: 'Beds, rooms, occupancy',
      }),
      n('Service Delivery', '/home-care', Home, {
        roles: ['EXEC', 'COORDINATOR', 'DON'],
        programs: ['SAH'],
        description: 'SAH service delivery workbench',
      }),
      n('SAH Readiness', '/home-care/readiness', ClipboardCheck, {
        roles: ['EXEC', 'COORDINATOR', 'FINANCE'],
        programs: ['SAH'],
        description: 'Clients blocked before delivery or financial activation',
      }),
      // SAH money leaves stay under Finance for FINANCE roles; keep Ops entry for
      // coordinators who manage package budgets day-to-day.
      n('SAH Budgets', '/home-care/budgets', PiggyBank, {
        roles: ['EXEC', 'COORDINATOR'],
        programs: ['SAH'],
        description: 'Package budgets, service drawdown, setup, claims and statements',
      }),
      n('Funding Changes', '/home-care/funding-changes', PiggyBank, {
        roles: ['EXEC', 'COORDINATOR', 'FINANCE'],
        programs: ['SAH'],
        description: 'Effective-dated payer changes, finance evidence and approvals',
      }),
      n('Workflows', '/workflows', Workflow, { roles: ['EXEC', 'COORDINATOR'] }),
      n('Regions', '/regions', Map, { roles: ['EXEC'] }),
      // Head Office → Cmd-K (density EXEC/MIXED ops ≤10).
    ],
  },
  {
    id: 'workforce',
    title: 'People',
    icon: UsersRound,
    description: 'People, onboarding, learning, compliance',
    items: [
      // 2026-08-19: People is the only Workforce L1. Workers, onboarding,
      // learning, policies, compliance, settings and contractors stay on
      // People tabs + Cmd-K (nav-graph deep links).
      n('People', '/people', UsersRound, {
        roles: ['COORDINATOR', 'EXEC', 'FACILITY'],
        additionalLiveRoles: [
          'REGISTERED_NURSE',
          'COMPLIANCE_OFFICER',
          'QUALITY_OFFICER',
          'FINANCE_OFFICER',
        ],
        badgeKey: 'people_cannot_work',
        description: 'Workers, onboarding, learning, policies and outstanding work',
      }),
    ],
  },
  {
    id: 'quality',
    title: 'Quality & Risk',
    icon: ShieldCheck,
    description: 'Incidents, complaints, audits, standards',
    items: complianceL1Items(['work-calendar', 'events', 'improvement-risk', 'standards-audit']),
  },
  {
    id: 'governance',
    title: 'Governance',
    icon: Landmark,
    description: 'Provider, board, registration, prudential',
    items: complianceL1Items(['governance-provider']),
  },
  {
    id: 'finance',
    title: 'Finance',
    icon: Wallet,
    description: 'Billing, claims, funding, budgets, payroll',
    items: [
      n('Finance Home', '/finance', Wallet, { roles: ['FINANCE', 'EXEC'] }),
      n('Invoices', '/finance/invoices', FileText, { roles: ['FINANCE', 'EXEC'] }),
      // Contributions is available to Support at Home and CHSP providers. CHSP
      // uses the OTHER firewall family, so programme admission remains in the
      // generated route policy rather than this RAC/SAH-only tag vocabulary.
      n('Contributions', '/finance/contributions', PiggyBank, {
        roles: ['FINANCE', 'EXEC'],
        description: 'Contribution policies, agreements and history',
      }),
      n('Claims dashboard', '/finance/claims', FileText, {
        badgeKey: 'claims_pending',
        roles: ['FINANCE', 'EXEC'],
        programs: ['RAC'],
        description: 'Residential and non-SAH claim status dashboard',
      }),
      n('SAH claims', '/home-care/claims', FileText, {
        badgeKey: 'claims_pending',
        roles: ['FINANCE', 'EXEC'],
        programs: ['SAH'],
        description: 'Services Australia batch preparation and submission tracking',
      }),
      n('Participant statements', '/home-care/statements', FileText, {
        roles: ['FINANCE', 'EXEC'],
        programs: ['SAH'],
        description: 'Monthly Support at Home participant statements',
      }),
      n('SAH reconciliation', '/home-care/reconciliation', Landmark, {
        roles: ['FINANCE', 'EXEC'],
        programs: ['SAH'],
        description: 'Visit confirmation, claims, remittance and month-close checks',
      }),
      n('Payroll', '/payroll', Banknote, { roles: ['FINANCE', 'EXEC'] }),
      n('AN-ACC Funding', '/finance/an-acc', Banknote, {
        roles: ['FINANCE', 'EXEC'],
        programs: ['RAC'],
      }),
      n('Residential billing', '/finance/residential-billing', Landmark, {
        roles: ['FINANCE', 'EXEC'],
        programs: ['RAC'],
      }),
      n('Budgets', '/finance/budgets', PieChart, {
        roles: ['FINANCE', 'EXEC', 'FACILITY'],
      }),
      // Density: accommodation, AN-ACC register, respite, DAP, deposits, pricing,
      // rate cards → Cmd-K (still live).
      // Overnight provider-managed billing build (2026-08-18): Billing run,
      // Payers, Charges, Statements are NOT added here — Finance is already
      // at the ≤10 visible-leaf ceiling for MIXED-provider FINANCE/EXEC
      // (nav-optimisation-phase1.test.ts; verified by adding "Billing" here
      // and watching that test fail at 11). They are reachable via the
      // `/finance` sub-page tab strip (sub-page-tabs-registry.ts) and Cmd-K
      // (nav-graph.ts), matching how billing-review/brokerage-invoices are
      // already handled.
    ],
  },
  {
    id: 'insights',
    title: 'Insights',
    icon: BarChart3,
    description: 'Analytics, reports, board pack',
    items: [
      n('Analytics Home', '/analytics', BarChart3, {
        roles: ['EXEC', 'QUALITY', 'FACILITY'],
      }),
      n('Reports', '/reports', FileBarChart, {
        // FINANCE: /reports is route-allowed for finance and payroll officers
        // (route-access.ts), who need exports; it had no menu entry for them.
        roles: ['EXEC', 'QUALITY', 'FACILITY', 'FINANCE'],
      }),
      n('Scheduled Reports', '/reports/subscriptions', CalendarClock, {
        roles: ['EXEC', 'QUALITY'],
      }),
      n('Activity', '/activity', Activity, {
        roles: ['EXEC', 'QUALITY', 'FACILITY', 'COORDINATOR'],
        description: 'Tenant-wide who-did-what feed from audit logs',
      }),
      n('Board Pack', '/analytics/board-pack', FileText, { roles: ['EXEC'] }),
      // Density: facility comparison, predictions, benchmarking → Cmd-K.
    ],
  },
] as const;

export const FOOTER_NAV_V2: readonly NavV2Item[] = [
  n('Settings', '/settings', Settings),
  n('Help', '/help', CircleHelp),
] as const;

// ─── Default pins per role-alias ──────────────────────────────────────────
// Port of data.jsx DEFAULT_PINS. Stored as live (post-rewrite) hrefs so the
// pinned list never points at a non-resolving prototype href.

export const DEFAULT_PINS_BY_ROLE: Readonly<Record<RoleAlias, readonly string[]>> = Object.freeze({
  // P1c: coordinator pins ⊂ KEEP L1. Home is the landing, not a pin.
  // People stays after #2123 People-only doors.
  COORDINATOR: ['/clients', '/roster/dispatch', '/compliance/incidents', '/tasks', '/people'],
  DON: [
    '/clients',
    '/medications/mar',
    '/clinical/alerts',
    '/compliance/incidents',
    '/observations',
  ],
  QUALITY: [
    '/compliance/incidents',
    '/compliance/work',
    '/compliance/continuous-improvement',
    '/analytics',
    '/compliance/standards',
  ],
  // P2a: facility-lead pins ⊂ KEEP L1. Executive View is admin-only (dead pin).
  // People stays after #2123 People-only doors.
  FACILITY: [
    '/residential',
    '/clients',
    '/roster/dispatch',
    '/compliance/incidents',
    '/tasks',
    '/people',
  ],
  FINANCE: [
    '/finance',
    '/finance/invoices',
    '/finance/claims',
    '/finance/an-acc',
    '/finance/residential-billing',
    '/payroll',
  ],
  EXEC: [
    '/dashboards/executive',
    '/analytics',
    '/analytics/board-pack',
    '/compliance/incidents',
    '/finance',
    '/people',
  ],
  // Pins must be NAV_V2 leaves (or me-* that exist as home items).
  CARE_WORKER: [
    '/me/roster',
    '/me/work-readiness',
    '/tasks',
    '/medications/mar',
    '/observations',
    '/clients',
  ],
});

/**
 * Amanda-style SAH pin packs (R1 residual decision 2026-08-04).
 * Max 6 pins. Applied only under an active SAH lens: MIXED/OTHER/UNKNOWN are
 * fail-closed and receive NO default pins (see defaultPinsForRoleAndProvider,
 * asserted in nav-v2-ia.test.ts). Several packs lean heavily on SAH-tagged
 * routes -- FINANCE is 5 of 6 -- which itemMatchesPrograms hides from an
 * untagged provider, so handing this pack to MIXED would pin items the user
 * cannot see.
 * P1c: COORDINATOR pins ⊂ KEEP L1 (readiness + People compliance folded).
 */
export const DEFAULT_SAH_PINS_BY_ROLE: Readonly<Record<RoleAlias, readonly string[]>> =
  Object.freeze({
    COORDINATOR: [
      '/clients',
      '/roster/dispatch',
      '/home-care/service-delivery',
      '/compliance/incidents',
      '/people',
      '/tasks',
    ],
    DON: [
      '/clients',
      '/care-plans',
      '/clinical/alerts',
      '/home-care/service-delivery',
      '/compliance/incidents',
      '/observations',
    ],
    QUALITY: [
      '/compliance/incidents',
      '/compliance/work',
      '/compliance/continuous-improvement',
      '/compliance/standards',
      '/analytics',
    ],
    FACILITY: [
      '/residential',
      '/clients',
      '/roster/dispatch',
      '/compliance/incidents',
      '/tasks',
      '/people',
    ],
    FINANCE: [
      '/home-care/budgets',
      '/home-care/claims',
      '/home-care/statements',
      '/home-care/readiness',
      '/home-care/funding-changes',
      '/finance/invoices',
    ],
    EXEC: [
      '/home-care/service-delivery',
      '/home-care/readiness',
      '/clients',
      '/compliance/incidents',
      '/people',
      '/home-care/budgets',
    ],
    CARE_WORKER: ['/tasks', '/clients', '/me/work-readiness', '/me/roster', '/observations'],
  });

export function defaultPinsForRoleAndProvider(
  role: RoleAlias,
  providerType: ProviderType,
): readonly string[] {
  if (providerType === 'SAH') {
    return DEFAULT_SAH_PINS_BY_ROLE[role];
  }
  if (providerType === 'RAC') return DEFAULT_PINS_BY_ROLE[role];
  return [];
}

// ─── Convenience flat-list (for Cmd-K consumers) ──────────────────────────

export interface FlatNavV2Item extends NavV2Item {
  groupId: NavV2GroupId;
  groupTitle: string;
  groupIcon: LucideIcon;
}

export function flattenNavV2(groups: readonly NavV2Group[] = NAV_V2): FlatNavV2Item[] {
  const out: FlatNavV2Item[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      out.push({ ...item, groupId: group.id, groupTitle: group.title, groupIcon: group.icon });
    }
  }
  return out;
}

/**
 * Reusable visibility check for arbitrary hrefs in contextual rails, WCP, etc.
 * Enforces the full authoritative chain used by sidebar/Cmd-K:
 *   - role (isRoleAllowedForPath)
 *   - module (isModuleNavigationVisible)
 *   - production-surface (isDashboardProductionSurfaceVisibleForTenant)
 *   - launch-primary curation (isLaunchPrimaryNavigationVisible)
 *   - tenant nav curation (tenantHiddenNavigationPaths)
 *
 * This directly addresses the 2026-06-14 audit P1 (gate parity): the previous
 * WCP and RelatedRecordsPanel only did a subset; new nav must use the full set
 * so pilot/blocked surfaces are never linked from new UX components.
 *
 * Programs/alias filtering is IA-specific and intentionally omitted here
 * (those are handled at sidebar construction time).
 */
export function isHrefVisible(
  href: string | undefined,
  ctx: {
    role?: string | null | undefined;
    billingPlan?: string | null | undefined;
    settingsDelegation?: SettingsDelegationPreference | null;
    settingsHiddenHrefs?: readonly string[];
    enabledModules?: string[] | null | undefined;
    tenantId?: string | null | undefined;
    isDemoTenant?: boolean | null | undefined;
    visibleGoLiveScope?: unknown;
    tenantHiddenNavigationPaths?: readonly string[] | null | undefined;
    governmentIntegrations?: GovernmentIntegrationEnablement | null | undefined;
  },
): boolean {
  if (!href) return false;
  // Strip query AND hash for pathname checks. The hash matters for in-page anchor targets used by
  // WorkflowCommandPanel actions (e.g. "/home-care/budgets#queue" or a bare "#budget-setup-queue").
  const pathname = href.split(/[?#]/)[0];
  // Empty path = a same-page target (pure "#anchor" or query-only) — always visible; it doesn't
  // navigate to another route, so the route/module/surface gates don't apply.
  if (!pathname) return true;

  // Fail closed on a missing role — matches sidebar/Cmd-K and the prior WCP gate
  // (isRoleAllowedForPath returns false for null/undefined). Without this, links
  // could flash during auth hydration or when the role is unset.
  const roleOk =
    (pathname === '/settings' || pathname.startsWith('/settings/')) &&
    !isSettingsDeferredOwnerPath(pathname)
      ? resolveSettingsAccess({
          leafId: pathname,
          actor: { role: ctx.role },
          delegation: ctx.settingsDelegation,
          context: { billingPlan: ctx.billingPlan, hiddenHrefs: ctx.settingsHiddenHrefs },
        }).visible
      : Boolean(ctx.role && isRoleAllowedForPath(pathname, ctx.role, ctx.billingPlan));
  const moduleOk = isModuleNavigationVisible(pathname, ctx.enabledModules ?? null, {
    isDemoTenant: ctx.isDemoTenant,
  });
  const surfaceOk = isDashboardProductionSurfaceVisibleForTenant(pathname, {
    tenantId: ctx.tenantId,
    isDemo: ctx.isDemoTenant,
  });
  const launchOk = isLaunchPrimaryNavigationVisible(pathname, ctx.visibleGoLiveScope, {
    isDemoTenant: ctx.isDemoTenant,
    includeDescendants: true,
  });
  const tenantNavOk = isTenantNavigationPathVisible(pathname, ctx.tenantHiddenNavigationPaths, {
    includeDescendants: true,
  });

  return (
    roleOk &&
    moduleOk &&
    surfaceOk &&
    launchOk &&
    tenantNavOk &&
    isGovernmentIntegrationPathVisible(pathname, ctx.governmentIntegrations, ctx.role)
  );
}

/** Full direct-door set shown only to compliance-only plan holders. */
export const COMPLIANCE_ONLY_NAV_GROUP: NavV2Group = {
  id: 'compliance-only',
  title: 'Compliance',
  icon: ShieldCheck,
  description: 'Compliance registers and provider governance',
  items: [
    ...(
      [
        ['/compliance/incidents', 'Events'],
        ['/compliance/complaints', 'Events'],
        ['/compliance/data-breach', 'Events'],
        ['/compliance/whistleblower', 'Events'],
        ['/compliance/incidents#incident-sirs-open-disclosure-workflow', 'Events'],
        ['/compliance/standards', 'Quality'],
        ['/compliance/audit-program', 'Quality'],
        ['/compliance/evidence-packs', 'Quality'],
        ['/compliance/continuous-improvement', 'Quality'],
        ['/compliance/restrictive-practices', 'Quality'],
        ['/compliance/ipc', 'Quality'],
        ['/compliance/whs', 'Quality'],
        ['/compliance/governance', 'Governance'],
        ['/compliance/governance/risk-register', 'Governance'],
        ['/compliance/governance/qcab', 'Governance'],
        ['/compliance/governance/conflicts', 'Governance'],
        ['/compliance/governance/skills-matrix', 'Governance'],
        ['/compliance/responsible-persons', 'Governance'],
        ['/compliance/registration', 'Governance'],
        ['/compliance/policies', 'Governance'],
        ['/compliance/documents', 'Governance'],
        ['/compliance/documents/expiring', 'Governance'],
        ['/compliance/work', 'Work'],
        ['/compliance/calendar', 'Work'],
        ['/compliance/reporting', 'Work'],
        ['/compliance/audit-trail', 'Work'],
        ['/people/compliance', 'People'],
        ['/people/compliance/screening', 'People'],
        ['/people/compliance/screening?view=requires-review', 'People'],
        ['/compliance/workforce/import', 'People'],
      ] as const
    ).map(([href, section]) => {
      const explicitLabels: Record<string, string> = {
        '/compliance/incidents': 'Care incidents',
        '/compliance/complaints': 'Complaints & feedback',
        '/compliance/data-breach': 'Data breach (NDB)',
        '/compliance/whistleblower': 'Whistleblower',
        '/compliance/incidents#incident-sirs-open-disclosure-workflow': 'Open disclosure',
        '/compliance/standards': 'Standards / Quality hub',
        '/compliance/audit-program': 'Audit program',
        '/compliance/evidence-packs': 'Evidence packs',
        '/compliance/continuous-improvement': 'Continuous improvement',
        '/compliance/restrictive-practices': 'Restrictive practices (+BSP)',
        '/compliance/ipc': 'IPC',
        '/compliance/whs': 'WHS',
        '/compliance/governance': 'Governance overview',
        '/compliance/governance/risk-register': 'Risk register',
        '/compliance/governance/qcab': 'QCAB',
        '/compliance/governance/conflicts': 'Conflicts',
        '/compliance/governance/skills-matrix': 'Skills matrix',
        '/compliance/responsible-persons': 'Responsible persons',
        '/compliance/registration': 'Registration & providers',
        '/compliance/policies': 'Policies',
        '/compliance/documents': 'Documents',
        '/compliance/documents/expiring': 'Expiring documents',
        '/compliance/work': 'My compliance work',
        '/compliance/calendar': 'Compliance calendar',
        '/compliance/reporting': 'Compliance reports',
        '/compliance/audit-trail': 'Audit trail',
        '/people/compliance': 'People compliance',
        '/people/compliance/screening': 'Worker screening',
        '/people/compliance/screening?view=requires-review': 'Verification queue',
        '/compliance/workforce/import': 'Workforce compliance import',
      };
      const item = href.includes('#')
        ? n(explicitLabels[href]!, href, MessageSquareWarning, { roles: ALL_ROLE_ALIASES })
        : COMPLIANCE_IA_ROUTES.some((route) => route.href === href)
          ? complianceNavItem(href, { roles: ALL_ROLE_ALIASES })
          : n(explicitLabels[href] ?? 'Compliance register', href, UsersRound, {
              roles: ALL_ROLE_ALIASES,
            });
      return { ...item, complianceGroupTitle: section };
    }),
  ],
};

/** Additional existing routes for the card layout; visibility gates remain authoritative. */
const complianceOnlyAdditionalItems: readonly NavV2Item[] = [
  n('WHS incidents', '/compliance/whs/incidents', ShieldAlert),
  // Feedback retains its former tab-specific role/module door via the alias redirect.
  n('Feedback', '/compliance/quality/feedback', MessageSquareWarning),
  complianceNavItem('/compliance/quality-indicators', { roles: ALL_ROLE_ALIASES }),
  n('Audit pack', '/compliance/audit-pack/export', ClipboardCheck),
  n('Provider obligations', '/compliance/registration?tab=obligations', BookOpen),
  n('Training evidence', '/people/learning', Award),
  n('WHS hazards', '/compliance/whs/hazards', ShieldAlert),
  n('Chemicals register', '/compliance/whs/chemicals', ShieldAlert),
  n('Equipment register', '/compliance/whs/equipment', ShieldAlert),
  n('Emergency drills', '/compliance/whs/drills', ShieldAlert),
  n('Psychosocial register', '/compliance/whs/psychosocial', ShieldAlert),
];

function complianceOnlyCardItems(hrefs: readonly string[]): readonly NavV2Item[] {
  return hrefs.map((href) => {
    const item = [...COMPLIANCE_ONLY_NAV_GROUP.items, ...complianceOnlyAdditionalItems].find(
      (candidate) => candidate.href === href,
    );
    if (!item) throw new Error(`Missing compliance-only navigation item: ${href}`);
    // The card itself supplies the section heading; keep badges and all visibility metadata.
    const labels: Record<string, string> = {
      '/compliance/incidents': 'Care incidents',
      '/compliance/complaints': 'Complaints',
      '/compliance/standards': 'Standards',
      '/compliance/whs': 'WHS hazards & registers',
    };
    return { ...item, label: labels[href] ?? item.label, complianceGroupTitle: undefined };
  });
}

/** Paul 30 September 2026: direct top-level cards for compliance-only plans. */
export const COMPLIANCE_ONLY_NAV_GROUPS: readonly NavV2Group[] = [
  {
    id: 'compliance-events',
    title: 'Events',
    icon: AlertTriangle,
    description: 'Incidents, complaints and disclosures',
    items: complianceOnlyCardItems([
      '/compliance/incidents',
      '/compliance/whs/incidents',
      '/compliance/complaints',
      '/compliance/quality/feedback',
      '/compliance/data-breach',
      '/compliance/incidents#incident-sirs-open-disclosure-workflow',
      '/compliance/whistleblower',
    ]),
  },
  {
    id: 'compliance-quality',
    title: 'Quality',
    icon: Award,
    description: 'Standards, improvement, audits and compliance work',
    items: complianceOnlyCardItems([
      '/compliance/standards',
      '/compliance/continuous-improvement',
      '/compliance/audit-program',
      '/compliance/audit-pack/export',
      '/compliance/evidence-packs',
      '/compliance/quality-indicators',
      '/compliance/restrictive-practices',
      '/compliance/work',
      '/compliance/calendar',
      '/compliance/reporting',
    ]),
  },
  {
    id: 'compliance-governance',
    title: 'Governance',
    icon: Landmark,
    description: 'Policies, risks and provider governance',
    items: complianceOnlyCardItems([
      '/compliance/policies',
      '/compliance/governance/risk-register',
      '/compliance/governance',
      '/compliance/governance/qcab',
      '/compliance/governance/conflicts',
      '/compliance/governance/skills-matrix',
      '/compliance/responsible-persons',
      '/compliance/registration',
      '/compliance/registration?tab=obligations',
      '/compliance/documents',
      '/compliance/documents/expiring',
      '/compliance/audit-trail',
    ]),
  },
  {
    id: 'compliance-people',
    title: 'People',
    icon: UsersRound,
    description: 'Screening, workforce evidence, WHS and IPC',
    items: complianceOnlyCardItems([
      '/people/compliance',
      '/people/compliance/screening',
      '/people/compliance/screening?view=requires-review',
      '/compliance/workforce/import',
      '/people/learning',
      '/compliance/whs',
      '/compliance/whs/hazards',
      '/compliance/whs/chemicals',
      '/compliance/whs/equipment',
      '/compliance/whs/drills',
      '/compliance/whs/psychosocial',
      '/compliance/ipc',
    ]),
  },
];
